<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <!-- Header avec barre de progression -->
      <div class="modal-header">
        <div class="header-content">
          <h3>📝 Nouvelle Demande Client</h3>
          <p class="step-indicator">Étape {{ currentStep }} sur {{ totalSteps }}</p>
        </div>
        <button @click="closeModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <!-- Barre de progression -->
      <div class="progress-container">
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="{ width: progressPercentage + '%' }"
          ></div>
        </div>
        <div class="steps-indicators">
          <div 
            v-for="(step, index) in steps" 
            :key="index"
            class="step-dot"
            :class="{
              'active': index + 1 === currentStep,
              'completed': index + 1 < currentStep,
              'upcoming': index + 1 > currentStep
            }"
          >
            <div class="step-number">
              <i v-if="index + 1 < currentStep" class="fas fa-check"></i>
              <span v-else>{{ index + 1 }}</span>
            </div>
            <span class="step-label">{{ step.label }}</span>
          </div>
        </div>
      </div>

      <!-- Body avec étapes -->
      <div class="modal-body">
        <form @submit.prevent="submitForm">
          <!-- Étape 1: Informations Client -->
          <div 
            v-show="currentStep === 1" 
            class="step-content"
            :class="{ 'step-active': currentStep === 1 }"
          >
            <div class="step-header">
              <div class="step-icon">
                <i class="fas fa-user"></i>
              </div>
              <div class="step-title">
                <h4>👤 Informations Client</h4>
                <p class="step-description">Renseignez les coordonnées du client</p>
              </div>
            </div>
            <div class="form-section">
            <div class="form-row">
              <div class="form-group client-autocomplete-group">
                <label>Nom et Prénom / Raison sociale *</label>
                <div class="autocomplete-wrapper">
                  <input 
                    v-model="form.client.nomComplet"
                    type="text"
                    required
                    placeholder="Ex: Dupont Jean ou Société SARL"
                    class="form-control"
                    @input="onClientSearch"
                    @focus="showClientSuggestions = true"
                    @blur="hideClientSuggestionsDelayed"
                    autocomplete="off"
                  >
                  <div class="autocomplete-icon" v-if="searchingClients">
                    <i class="fas fa-spinner fa-spin"></i>
                  </div>
                  <div class="autocomplete-icon" v-else-if="selectedClient">
                    <i class="fas fa-check-circle text-success"></i>
                  </div>
                  <!-- Liste des suggestions -->
                  <div 
                    v-if="showClientSuggestions && clientSuggestions.length > 0" 
                    class="autocomplete-dropdown"
                  >
                    <div class="dropdown-header">
                      <i class="fas fa-users"></i>
                      <span>Clients existants ({{ clientSuggestions.length }})</span>
                    </div>
                    <div 
                      v-for="client in clientSuggestions" 
                      :key="client._id"
                      class="autocomplete-item"
                      @mousedown.prevent="selectClient(client)"
                    >
                      <div class="client-avatar">
                        <i class="fas fa-user"></i>
                      </div>
                      <div class="client-info">
                        <div class="client-name">{{ client.nomComplet }}</div>
                        <div class="client-details">
                          <span><i class="fas fa-phone"></i> {{ client.telephone }}</span>
                          <span v-if="client.adresse?.ville"><i class="fas fa-map-marker-alt"></i> {{ client.adresse.ville }}</span>
                        </div>
                      </div>
                      <div class="client-select-icon">
                        <i class="fas fa-chevron-right"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <small v-if="selectedClient" class="client-selected-hint">
                  <i class="fas fa-check-circle"></i>
                  Client existant sélectionné - informations remplies automatiquement
                </small>
              </div>
              <div class="form-group">
                <label>Matricule Fiscal</label>
                <input 
                  v-model="form.client.matriculeFiscal"
                  type="text"
                  placeholder="Ex: 1234567/A/M/000"
                  class="form-control"
                >
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>Login</label>
                <input 
                  v-model="form.client.codeLog"
                  type="text"
                  placeholder="Ex: LOG-12345"
                  class="form-control"
                >
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>Téléphone *</label>
                <input 
                  v-model="form.client.telephone"
                  type="tel"
                  required
                  class="form-control"
                >
              </div>
              <div class="form-group">
                <label>Email</label>
                <input 
                  v-model="form.client.email"
                  type="email"
                  class="form-control"
                >
              </div>
            </div>

            <div class="form-group">
              <label>Adresse *</label>
              <input 
                v-model="form.client.adresse.rue"
                type="text"
                placeholder="Rue"
                required
                class="form-control"
              >
              <div class="form-row" style="margin-top: 0.5rem;">
                <input 
                  v-model="form.client.adresse.codePostal"
                  type="text"
                  placeholder="Code postal"
                  class="form-control"
                  style="flex: 1;"
                >
                <div class="gouvernorat-select-wrapper" style="flex: 2;" :class="{ 'has-selection': form.client.adresse.ville }">
                  <div class="gouvernorat-select-icon">
                    <i class="fas fa-map-marker-alt"></i>
                  </div>
                  <select 
                    v-model="form.client.adresse.ville"
                    class="form-control gouvernorat-select"
                  >
                    <option value="">📍 Sélectionner un ville</option>
                    <optgroup label="🗺️ Nord-Est">
                      <option value="Tunis">Tunis</option>
                      <option value="Ariana">Ariana</option>
                      <option value="Ben Arous">Ben Arous</option>
                      <option value="Manouba">Manouba</option>
                      <option value="Zaghouan">Zaghouan</option>
                      <option value="Bizerte">Bizerte</option>
                    </optgroup>
                    <optgroup label="🗺️ Nord-Ouest">
                      <option value="Jendouba">Jendouba</option>
                      <option value="Béja">Béja</option>
                      <option value="Le Kef">Le Kef</option>
                      <option value="Siliana">Siliana</option>
                    </optgroup>
                    <optgroup label="🗺️ Centre-Est">
                      <option value="Nabeul">Nabeul</option>
                      <option value="Sousse">Sousse</option>
                      <option value="Monastir">Monastir</option>
                      <option value="Mahdia">Mahdia</option>
                    </optgroup>
                    <optgroup label="🗺️ Sud">
                      <option value="Sfax">Sfax</option>
                      <option value="Gabès">Gabès</option>
                      <option value="Médenine">Médenine</option>
                      <option value="Tataouine">Tataouine</option>
                      <option value="Kairouan">Kairouan</option>
                      <option value="Kasserine">Kasserine</option>
                      <option value="Sidi Bouzid">Sidi Bouzid</option>
                      <option value="Gafsa">Gafsa</option>
                      <option value="Tozeur">Tozeur</option>
                      <option value="Kébili">Kébili</option>
                    </optgroup>
                  </select>
                  <div class="gouvernorat-select-arrow">
                    <i class="fas fa-chevron-down"></i>
                  </div>
                  <div v-if="form.client.adresse.ville" class="gouvernorat-selection-indicator">
                    <i class="fas fa-check-circle"></i>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Prix *</label>
                <input 
                  v-model="form.prix"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  required
                  class="form-control"
                  @input="clearError"
                >
              </div>
            </div>
            </div>
          </div>

          <!-- Étape 2: Détails de l'Intervention -->
          <div 
            v-show="currentStep === 2" 
            class="step-content"
            :class="{ 'step-active': currentStep === 2 }"
          >
            <div class="step-header">
              <div class="step-icon">
                <i class="fas fa-tools"></i>
              </div>
              <div class="step-title">
                <h4>🔧 Détails de l'Operations</h4>
                <p class="step-description">Spécifiez le type d'intervention et les détails techniques</p>
              </div>
            </div>
            <div class="form-section">
            <div class="form-row">
              <div class="form-group">
                <label>Type operation *</label>
                <div class="intervention-selector-wrapper" :class="{ 'has-selection': form.typeIntervention, 'error': !form.typeIntervention }">
                  <div class="intervention-selector-icon">
                    <i class="fas fa-tools"></i>
                  </div>
                  <select v-model="form.typeIntervention" required class="form-control">
                    <option value="">Sélectionner le type...</option>
                    <option value="Installation">🔧 Installation</option>
                    <option value="Intervention">⚙️ Intervention</option>
                    <option value="Prospect">💼 Prospect</option>
                    <option v-if="userRole === 'Admin'" value="Recouvrement">💰 Recouvrement</option>
                  </select>
                  <div class="intervention-selector-arrow">
                    <i class="fas fa-chevron-down"></i>
                  </div>
                  <div v-if="form.typeIntervention" class="intervention-selection-indicator">
                    <i class="fas fa-check-circle"></i>
                  </div>
                  <div v-if="form.typeIntervention" class="intervention-selection-badge">1</div>
                </div>
              </div>
              <div class="form-group">
                <label>Priorité</label>
                <div class="priority-selector-wrapper" :class="{ 'has-selection': form.priorite }">
                  <div class="priority-selector-icon">
                    <i class="fas fa-exclamation-triangle"></i>
                  </div>
                  <select v-model="form.priorite" class="form-control">
                    <option value="Normale">🟡 Normale</option>
                    <option value="Basse">🟢 Basse</option>
                    <option value="Haute">🟠 Haute</option>
                    <option value="Urgente">🔴 Urgente</option>
                  </select>
                  <div class="priority-selector-arrow">
                    <i class="fas fa-chevron-down"></i>
                  </div>
                  <div v-if="form.priorite && form.priorite !== 'Normale'" class="priority-selection-indicator">
                    <i class="fas fa-check-circle"></i>
                  </div>
                  <div v-if="form.priorite && form.priorite !== 'Normale'" class="priority-selection-badge">1</div>
                </div>
              </div>
            </div>

            <!-- Section Recouvrement (visible uniquement si type = Recouvrement) -->
            <div v-if="form.typeIntervention === 'Recouvrement'" class="recouvrement-section">
              <div class="recouvrement-header">
                <div class="recouvrement-icon">
                  <i class="fas fa-hand-holding-usd"></i>
                </div>
                <div class="recouvrement-title">
                  <h4>💰 Éléments à recouvrer</h4>
                  <p>Sélectionnez les éléments concernés par ce recouvrement</p>
                </div>
              </div>
              
              <div class="recouvrement-checklist">
                <label class="recouvrement-item" :class="{ 'checked': form.recouvrementChecklist.abonnement }">
                  <input 
                    type="checkbox" 
                    v-model="form.recouvrementChecklist.abonnement"
                    class="recouvrement-checkbox"
                  >
                  <div class="recouvrement-item-content">
                    <div class="recouvrement-item-icon">
                      <i class="fas fa-file-contract"></i>
                    </div>
                    <div class="recouvrement-item-info">
                      <span class="recouvrement-item-title">Abonnement</span>
                      <span class="recouvrement-item-desc">Recouvrement des frais d'abonnement</span>
                    </div>
                    <div class="recouvrement-item-check">
                      <i class="fas fa-check"></i>
                    </div>
                  </div>
                </label>
                
                <label class="recouvrement-item" :class="{ 'checked': form.recouvrementChecklist.sim }">
                  <input 
                    type="checkbox" 
                    v-model="form.recouvrementChecklist.sim"
                    class="recouvrement-checkbox"
                  >
                  <div class="recouvrement-item-content">
                    <div class="recouvrement-item-icon">
                      <i class="fas fa-sim-card"></i>
                    </div>
                    <div class="recouvrement-item-info">
                      <span class="recouvrement-item-title">SIM</span>
                      <span class="recouvrement-item-desc">Recouvrement de la carte SIM</span>
                    </div>
                    <div class="recouvrement-item-check">
                      <i class="fas fa-check"></i>
                    </div>
                  </div>
                </label>
                
                <label class="recouvrement-item" :class="{ 'checked': form.recouvrementChecklist.trancheCreance }">
                  <input 
                    type="checkbox" 
                    v-model="form.recouvrementChecklist.trancheCreance"
                    class="recouvrement-checkbox"
                  >
                  <div class="recouvrement-item-content">
                    <div class="recouvrement-item-icon">
                      <i class="fas fa-coins"></i>
                    </div>
                    <div class="recouvrement-item-info">
                      <span class="recouvrement-item-title">Tranche Créance</span>
                      <span class="recouvrement-item-desc">Recouvrement d'une tranche de créance</span>
                    </div>
                    <div class="recouvrement-item-check">
                      <i class="fas fa-check"></i>
                    </div>
                  </div>
                </label>
              </div>
              
              <div v-if="!hasRecouvrementSelection" class="recouvrement-warning">
                <i class="fas fa-exclamation-triangle"></i>
                <span>Veuillez sélectionner au moins un élément à recouvrer</span>
              </div>
            </div>

            <!-- Liste des appareils ajoutés (masqué si Recouvrement) -->
            <div v-if="form.typeIntervention !== 'Recouvrement' && form.appareils.length > 0" class="appareils-list">
              <label class="list-label">
                <i class="fas fa-list"></i>
                Appareils demandés ({{ form.appareils.length }})
              </label>
              <div 
                v-for="(appareil, index) in form.appareils" 
                :key="index"
                class="appareil-item"
              >
                <div class="appareil-info">
                  <span class="appareil-icon">{{ getAppareilIcon(appareil.type) }}</span>
                  <span class="appareil-name">{{ appareil.type }}</span>
                  <span class="appareil-quantity">× {{ appareil.quantite }}</span>
                </div>
                <button 
                  type="button"
                  @click="removeAppareil(index)"
                  class="btn-remove-appareil"
                  title="Supprimer cet appareil"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>

            <!-- Formulaire d'ajout d'appareil (masqué si Recouvrement) -->
            <div v-if="form.typeIntervention !== 'Recouvrement'" class="add-appareil-form">
              <label class="form-label-section">
                <i class="fas fa-plus-circle"></i>
                Ajouter un type d'appareil
              </label>
              
              <div class="form-row">
                <div class="form-group" style="flex: 2;">
                  <label>Type d'appareil *</label>
                  <div class="appareil-selector-wrapper" :class="{ 'has-selection': currentAppareil.type }">
                    <div class="appareil-selector-icon">
                      <i class="fas fa-microchip"></i>
                    </div>
                    <select v-model="currentAppareil.type" @change="handleAppareilTypeChange" class="form-control" :disabled="loadingCategories">
                      <option value="">{{ loadingCategories ? '⏳ Chargement...' : '✨ Sélectionner l\'appareil...' }}</option>
                      <option v-for="category in categories" :key="category._id" :value="category.nom">
                        {{ category.emoji }} {{ category.nom }}
                      </option>
                     
                    </select>
                    <div class="appareil-selector-arrow">
                      <i class="fas fa-chevron-down"></i>
                    </div>
                    <div v-if="currentAppareil.type && currentAppareil.type !== 'Autre'" class="appareil-selection-indicator">
                      <i class="fas fa-check-circle"></i>
                    </div>
                  </div>
                </div>
                <div class="form-group" style="flex: 1;">
                  <label>Quantité *</label>
                  <input 
                    v-model.number="currentAppareil.quantite"
                    type="number"
                    min="1"
                    placeholder="1"
                    class="form-control"
                  >
                </div>
              </div>

              <!-- Champ personnalisé si "Autre" est sélectionné -->
              <div v-if="currentAppareil.type === 'Autre'" class="form-row custom-appareil-row">
                <div class="form-group">
                  <label>Nom du type d'appareil *</label>
                  <div class="custom-input-wrapper">
                    <i class="fas fa-edit"></i>
                    <input 
                      v-model="currentAppareil.customName" 
                      type="text" 
                      class="form-control"
                      placeholder="Ex: Routeur, Modem, etc..."
                    >
                  </div>
                </div>
              </div>

              <button 
                type="button"
                @click="addAppareil"
                class="btn btn-add-appareil"
                :disabled="!canAddAppareil"
              >
                <i class="fas fa-plus"></i>
                Ajouter cet appareil
              </button>
            </div>

            <div class="form-group">
              <label>Description</label>
              <textarea 
                v-model="form.description"
                rows="3"
                placeholder="Détails de l'intervention..."
                class="form-control"
              ></textarea>
            </div>

            <div class="form-group">
              <label>Commentaires</label>
              <textarea 
                v-model="form.commentaires"
                rows="2"
                placeholder="Commentaires additionnels..."
                class="form-control"
              ></textarea>
            </div>
            </div>
          </div>

          <!-- Étape 4: Affectation Technicien -->
          <div 
            v-show="currentStep === 4" 
            class="step-content"
            :class="{ 'step-active': currentStep === 4 }"
          >
            <div class="step-header">
              <div class="step-icon">
                <i class="fas fa-user-cog"></i>
              </div>
              <div class="step-title">
                <h4>👨‍🔧 Affectation Technicien</h4>
                <p class="step-description">Assignez un technicien ou laissez le planning le faire</p>
              </div>
            </div>
            <div class="form-section">
            <p class="form-help">⚠️ L'affectation d'un technicien est obligatoire pour créer la demande</p>
            <div class="form-group">
              <label>Technicien disponible *</label>
              <div class="technician-selector-wrapper" :class="{ 'has-selection': form.technicienAffecte, 'error': !form.technicienAffecte }">
                <div class="technician-selector-icon">
                  <i class="fas fa-user-cog"></i>
                </div>
                <select v-model="form.technicienAffecte" class="form-control" required>
                  <option value="">Sélectionner un technicien...</option>
                  <option 
                    v-for="technicien in techniciens" 
                    :key="technicien._id" 
                    :value="technicien._id"
                  >
                    🔧 {{ technicien.prenom }} {{ technicien.nom }}
                  </option>
                </select>
                <div class="technician-selector-arrow">
                  <i class="fas fa-chevron-down"></i>
                </div>
                <div v-if="form.technicienAffecte" class="technician-selection-indicator">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div v-if="form.technicienAffecte" class="technician-selection-badge">1</div>
              </div>
              <small v-if="form.technicienAffecte" class="text-success">
                <i class="fas fa-check-circle"></i>
                Technicien affecté
              </small>
              <small v-else class="text-danger">
                <i class="fas fa-exclamation-triangle"></i>
                Veuillez sélectionner un technicien
              </small>
            </div>
            </div>
          </div>

          <!-- Étape 3: Planning -->
          <div 
            v-show="currentStep === 3" 
            class="step-content"
            :class="{ 'step-active': currentStep === 3 }"
          >
            <div class="step-header">
              <div class="step-icon">
                <i class="fas fa-calendar"></i>
              </div>
              <div class="step-title">
                <h4>📅 Créneaux Proposés</h4>
                <p class="step-description">Proposez des dates et heures pour l'intervention</p>
              </div>
            </div>
            <div class="form-section">
            <p class="form-help">Spécifiez la date de sortie du technicien et les heures d'intervention. La date de création est automatiquement enregistrée.</p>
            <div class="dates-container">
              <div 
                v-for="(date, index) in form.datesProposes" 
                :key="index"
                class="date-row"
                :class="{ 'date-row-invalid': !date.dateSortie || date.dateSortie.trim() === '' || !date.heureDebut || date.heureDebut.trim() === '' || !date.heureFin || date.heureFin.trim() === '' }"
              >
                <div class="form-group">
                  <label class="form-label-small">Date de sortie *</label>
                  <input 
                    v-model="date.dateSortie"
                    type="date"
                    class="form-control"
                    :class="{ 'is-invalid': !date.dateSortie || date.dateSortie.trim() === '' }"
                    :min="minDate"
                    required
                    title="Date de sortie du technicien chez le client (obligatoire)"
                    @input="clearError"
                  >
                </div>
                <div class="form-group">
                  <label class="form-label-small">Heure début *</label>
                  <input 
                    v-model="date.heureDebut"
                    type="time"
                    class="form-control"
                    :class="{ 'is-invalid': isTimePastForToday(date.dateSortie, date.heureDebut) }"
                    required
                  >
                  <small v-if="isTimePastForToday(date.dateSortie, date.heureDebut)" class="text-danger">
                    <i class="fas fa-exclamation-triangle"></i>
                    Cette heure est déjà passée aujourd'hui
                  </small>
                  <small v-else-if="date.dateSortie === minDate" class="text-info">
                    <i class="fas fa-info-circle"></i>
                    Heure minimale: {{ getMinTimeForDate(date.dateSortie) }}
                  </small>
                </div>
                <div class="form-group">
                  <label class="form-label-small">Heure fin *</label>
                  <input 
                    v-model="date.heureFin"
                    type="time"
                    class="form-control"
                    required
                  >
                </div>
                <button 
                  type="button"
                  @click="removeDate(index)"
                  class="btn-remove"
                  v-if="form.datesProposes.length > 1"
                  title="Supprimer ce créneau"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
            <button 
              type="button"
              @click="addDate"
              class="btn btn-secondary btn-sm"
            >
              <i class="fas fa-plus"></i>
              Ajouter un créneau
            </button>
            <div class="dates-summary" v-if="validDatesCount > 0">
              <small class="text-success">
                <i class="fas fa-check-circle"></i>
                {{ validDatesCount }} créneau{{ validDatesCount > 1 ? 'x' : '' }} valide{{ validDatesCount > 1 ? 's' : '' }}
              </small>
            </div>
            </div>
          </div>
        </form>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="error-banner">
        <div class="error-content">
          <i class="fas fa-exclamation-triangle"></i>
          <span>{{ errorMessage }}</span>
          <button @click="errorMessage = ''" class="error-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Footer avec navigation -->
      <div class="modal-footer">
        <div class="footer-left">
          <button @click="closeModal" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Annuler
          </button>
        </div>
        
        <div class="footer-right">
          <button 
            v-if="currentStep > 1"
            @click="previousStep" 
            class="btn btn-outline"
          >
            <i class="fas fa-chevron-left"></i>
            Précédent
          </button>
          
          <button 
            v-if="currentStep < totalSteps"
            @click="nextStep"
            class="btn btn-primary"
            :disabled="!isStepValid"
          >
            Suivant
            <i class="fas fa-chevron-right"></i>
          </button>
          
          <button 
            v-if="currentStep === totalSteps"
            @click="submitForm"
            class="btn btn-success"
            :disabled="submitting || !isFormValid"
          >
            <i class="fas fa-save"></i>
            {{ submitting ? 'Création...' : 'Créer la demande' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toastification'
import api from '@/services/api'

export default {
  name: 'CreateProspectModal',
  emits: ['close', 'prospect-created'],
  setup(props, { emit }) {
    const store = useStore()
    const toast = useToast()

    const submitting = ref(false)
    const errorMessage = ref('')
    const techniciens = ref([])
    
    // Autocomplétion clients
    const clientSuggestions = ref([])
    const showClientSuggestions = ref(false)
    const searchingClients = ref(false)
    const selectedClient = ref(null)
    let searchTimeout = null
    const categories = ref([])
    const loadingCategories = ref(false)
    const currentStep = ref(1)
    const totalSteps = 4
    const userRole = computed(() => store.getters['auth/userRole'])

    // Appareil en cours d'ajout
    const currentAppareil = ref({
      type: '',
      quantite: 1,
      customName: ''
    })

    const steps = [
      { label: 'Client', icon: 'fa-user' },
      { label: 'Intervention', icon: 'fa-tools' },
      { label: 'Planning', icon: 'fa-calendar' },
      { label: 'Technicien', icon: 'fa-user-cog' }
    ]

    const form = ref({
      client: {
        nomComplet: '',
        telephone: '',
        email: '',
        matriculeFiscal: '',
        codeLog: '',
        adresse: {
          rue: '',
          ville: '',
          codePostal: '',
          region: ''
        }
      },
      prix: '',
      typeIntervention: '',
      priorite: 'Normale',
      appareils: [], // Liste des appareils demandés
      // Checklist Recouvrement
      recouvrementChecklist: {
        abonnement: false,
        sim: false,
        trancheCreance: false
      },
      description: '',
      commentaires: '',
      technicienAffecte: '', // Technicien optionnel affecté par le commercial
      datesProposes: [
        {
          heureDebut: '09:00',
          heureFin: '10:00', // heureDebut + 1h par défaut
          dateSortie: '' // Date de sortie du technicien chez le client
        }
      ]
    })
    
    // Computed pour vérifier si au moins un élément de recouvrement est sélectionné
    const hasRecouvrementSelection = computed(() => {
      return form.value.recouvrementChecklist.abonnement || 
             form.value.recouvrementChecklist.sim || 
             form.value.recouvrementChecklist.trancheCreance
    })

    const minDate = computed(() => {
      // Utiliser le fuseau horaire de Tunis
      const nowTunis = new Date().toLocaleString('en-US', { timeZone: 'Africa/Tunis' })
      return new Date(nowTunis).toISOString().split('T')[0]
    })

    // Fonction pour vérifier si une heure est passée pour aujourd'hui (heure de Tunis)
    const isTimePastForToday = (dateSortie, heureDebut) => {
      if (!dateSortie || !heureDebut) return false
      
      // Obtenir la date et l'heure actuelles à Tunis
      const nowTunis = new Date().toLocaleString('en-US', { timeZone: 'Africa/Tunis' })
      const tunisDate = new Date(nowTunis)
      const today = tunisDate.toISOString().split('T')[0]
      
      if (dateSortie !== today) return false // Pas aujourd'hui, donc OK
      
      // Comparer les heures
      const [hours, minutes] = heureDebut.split(':').map(Number)
      const selectedTime = new Date(tunisDate)
      selectedTime.setHours(hours, minutes, 0, 0)
      
      return selectedTime <= tunisDate // Retourne true si l'heure est passée
    }

    // Fonction pour obtenir l'heure minimale pour une date donnée (heure de Tunis)
    const getMinTimeForDate = (dateSortie) => {
      if (!dateSortie) return '09:00'
      
      // Obtenir la date et l'heure actuelles à Tunis
      const nowTunis = new Date().toLocaleString('en-US', { timeZone: 'Africa/Tunis' })
      const tunisDate = new Date(nowTunis)
      const today = tunisDate.toISOString().split('T')[0]
      
      if (dateSortie !== today) return '09:00' // Pas aujourd'hui, heure min normale
      
      // Pour aujourd'hui, l'heure min est l'heure actuelle + 1h
      tunisDate.setHours(tunisDate.getHours() + 1)
      const hours = String(tunisDate.getHours()).padStart(2, '0')
      const minutes = String(Math.ceil(tunisDate.getMinutes() / 15) * 15).padStart(2, '0') // Arrondir aux 15 min
      
      return `${hours}:${minutes === '60' ? '00' : minutes}`
    }

    const validDatesCount = computed(() => {
      return form.value.datesProposes.filter(date => 
        date.dateSortie && date.dateSortie.trim() !== '' &&
        date.heureDebut && date.heureDebut.trim() !== '' &&
        date.heureFin && date.heureFin.trim() !== '' &&
        !isTimePastForToday(date.dateSortie, date.heureDebut) // Vérifier que l'heure n'est pas passée
      ).length
    })

    const progressPercentage = computed(() => {
      return ((currentStep.value - 1) / (totalSteps - 1)) * 100
    })

    const isStepValid = computed(() => {
      switch (currentStep.value) {
        case 1:
          return form.value.client.nomComplet && 
                 form.value.client.telephone && 
                 form.value.client.adresse.rue && 
                 (form.value.prix !== '' && form.value.prix >= 0)
        case 2:
          // Pour "Intervention", les appareils ne sont pas obligatoires
          if (form.value.typeIntervention === 'Intervention') {
            return form.value.typeIntervention
          }
          // Pour "Recouvrement", au moins un élément doit être sélectionné
          if (form.value.typeIntervention === 'Recouvrement') {
            return hasRecouvrementSelection.value
          }
          // Pour les autres types, au moins un appareil est requis
          return form.value.typeIntervention && form.value.appareils.length > 0
        case 3:
          return validDatesCount.value > 0 // Planning (dates obligatoires)
        case 4:
          return form.value.technicienAffecte !== '' && form.value.technicienAffecte !== null // Technicien OBLIGATOIRE
        default:
          return false
      }
    })

    const isFormValid = computed(() => {
      const baseValid = form.value.client.nomComplet &&
             form.value.client.telephone &&
             form.value.client.adresse.rue &&
             (form.value.prix !== '' && form.value.prix >= 0) &&
             form.value.typeIntervention &&
             validDatesCount.value > 0 &&
             form.value.technicienAffecte !== '' && form.value.technicienAffecte !== null // Technicien OBLIGATOIRE
      
      // Pour "Intervention", les appareils ne sont pas obligatoires
      if (form.value.typeIntervention === 'Intervention') {
        return baseValid
      }
      // Pour "Recouvrement", au moins un élément doit être sélectionné
      if (form.value.typeIntervention === 'Recouvrement') {
        return baseValid && hasRecouvrementSelection.value
      }
      // Pour les autres types, au moins un appareil est requis
      return baseValid && form.value.appareils.length > 0
    })

    const canAddAppareil = computed(() => {
      if (!currentAppareil.value.type || !currentAppareil.value.quantite || currentAppareil.value.quantite < 1) {
        return false
      }
      if (currentAppareil.value.type === 'Autre' && !currentAppareil.value.customName) {
        return false
      }
      return true
    })

    const handleAppareilTypeChange = () => {
      // Réinitialiser le champ personnalisé si on change de type
      if (currentAppareil.value.type !== 'Autre') {
        currentAppareil.value.customName = ''
      }
    }

    const addAppareil = () => {
      if (!canAddAppareil.value) {
        toast.warning('Veuillez remplir tous les champs de l\'appareil')
        return
      }

      const appareilType = currentAppareil.value.type === 'Autre' 
        ? currentAppareil.value.customName 
        : currentAppareil.value.type

      form.value.appareils.push({
        type: appareilType,
        quantite: currentAppareil.value.quantite
      })

      // Réinitialiser le formulaire d'ajout
      currentAppareil.value = {
        type: '',
        quantite: 1,
        customName: ''
      }

      toast.success(`Appareil ajouté (${form.value.appareils.length} au total)`)
    }

    const removeAppareil = (index) => {
      form.value.appareils.splice(index, 1)
      toast.info('Appareil supprimé')
    }

    const getAppareilIcon = (type) => {
      // Chercher l'emoji de la catégorie correspondante
      const category = categories.value.find(cat => cat.nom === type)
      if (category) {
        return category.emoji
      }
      // Fallback pour les types personnalisés
      return '📦'
    }

    const nextStep = () => {
      if (currentStep.value < totalSteps && isStepValid.value) {
        currentStep.value++
        toast.info(`Étape ${currentStep.value}/${totalSteps}`)
      }
    }

    const previousStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--
      }
    }

    const addDate = () => {
      form.value.datesProposes.push({
        heureDebut: '09:00',
        heureFin: '10:00', // heureDebut + 1h par défaut
        dateSortie: ''
      })
    }

    const removeDate = (index) => {
      form.value.datesProposes.splice(index, 1)
    }

    const submitForm = async () => {
      // Réinitialiser le message d'erreur
      errorMessage.value = ''
      
      if (!isFormValid.value) {
        errorMessage.value = 'Veuillez remplir tous les champs obligatoires (client, intervention, planning et technicien)'
        toast.error('Veuillez remplir tous les champs obligatoires (client, intervention, planning et technicien)')
        return
      }
      
      // Vérifier spécifiquement le technicien
      if (!form.value.technicienAffecte) {
        errorMessage.value = 'L\'affectation d\'un technicien est obligatoire'
        toast.error('L\'affectation d\'un technicien est obligatoire')
        currentStep.value = 4 // Retourner à l'étape technicien
        return
      }

      // Vérifier qu'aucune heure passée n'est sélectionnée
      const hasPassedTime = form.value.datesProposes.some(date => 
        isTimePastForToday(date.dateSortie, date.heureDebut)
      )
      
      if (hasPassedTime) {
        errorMessage.value = 'Impossible de sélectionner une heure déjà passée pour aujourd\'hui'
        toast.error('Impossible de sélectionner une heure déjà passée pour aujourd\'hui')
        return
      }

      submitting.value = true
      try {
        // Nettoyer l'email s'il est invalide
        const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/
        const cleanedForm = { ...form.value }
        if (cleanedForm.client.email && !emailRegex.test(cleanedForm.client.email)) {
          console.log('Email invalide détecté, suppression:', cleanedForm.client.email)
          cleanedForm.client.email = '' // Vider l'email invalide
        }

        // Créer les dates avec la date de création automatique
        // Utiliser une fonction pour créer une date UTC à midi pour éviter les problèmes de fuseau horaire
        const createDateUTC = (dateString) => {
          // dateString est au format "YYYY-MM-DD"
          const [year, month, day] = dateString.split('-').map(Number)
          // Créer une date UTC à midi pour éviter les décalages de fuseau horaire
          return new Date(Date.UTC(year, month - 1, day, 12, 0, 0))
        }
        
        const datesProposes = form.value.datesProposes
          .filter(date => 
            date.dateSortie && date.dateSortie.trim() !== '' &&
            date.heureDebut && date.heureDebut.trim() !== '' &&
            date.heureFin && date.heureFin.trim() !== ''
          )
          .map(date => ({
            date: new Date(), // Date de création automatique
            heureDebut: date.heureDebut,
            heureFin: date.heureFin,
            dateSortie: createDateUTC(date.dateSortie),
            disponible: true
          }))

        // S'assurer qu'il y a au moins une date
        if (datesProposes.length === 0) {
          datesProposes.push({
            date: new Date(),
            heureDebut: '09:00',
            heureFin: '12:00',
            dateSortie: new Date(),
            disponible: true
          })
        }

        // Calculer le nombre total d'appareils
        const nombreAppareils = form.value.appareils.reduce((total, app) => total + app.quantite, 0)
        
        // Préparer le type d'appareil pour la compatibilité (premier appareil de la liste)
        const typeAppareil = form.value.appareils.length > 0 ? form.value.appareils[0].type : ''

        const formData = {
          ...cleanedForm,
          typeAppareil: typeAppareil,
          nombreAppareils: nombreAppareils,
          appareils: form.value.appareils, // Liste complète des appareils
          datesProposes,
          // Le technicien est maintenant obligatoire
          technicienAffecte: cleanedForm.technicienAffecte
        }
        
        console.log('Données finales à envoyer:', formData)

        // Vérifier qu'il reste au moins une date valide
        if (formData.datesProposes.length === 0) {
          errorMessage.value = 'Veuillez saisir au moins une date valide'
          toast.error('Veuillez saisir au moins une date valide')
          return
        }

        // Sauvegarder ou mettre à jour le client automatiquement
        try {
          await api.post('/clients/find-or-create', {
            nomComplet: cleanedForm.client.nomComplet,
            telephone: cleanedForm.client.telephone,
            email: cleanedForm.client.email,
            matriculeFiscal: cleanedForm.client.matriculeFiscal,
            codeLog: cleanedForm.client.codeLog,
            adresse: cleanedForm.client.adresse
          })
        } catch (clientError) {
          console.log('Info: Client non sauvegardé séparément:', clientError.message)
        }

        const response = await api.post('/prospects', formData)
        
        toast.success('✅ Demande créée avec succès')
        emit('prospect-created', response.data.prospect)
      } catch (error) {
        console.error('Erreur lors de la création du prospect:', error)
        const connectionError = 'Erreur de connexion au serveur'
        errorMessage.value = connectionError
        toast.error(connectionError)
      } finally {
        submitting.value = false
      }
    }

    const closeModal = () => {
      // Réinitialiser le formulaire et les erreurs
      errorMessage.value = ''
      emit('close')
    }

    // Réinitialiser l'erreur quand l'utilisateur modifie le formulaire
    const clearError = () => {
      if (errorMessage.value) {
        errorMessage.value = ''
      }
    }

    // ===== AUTOCOMPLÉTION CLIENTS =====
    
    // Recherche de clients avec debounce
    const onClientSearch = () => {
      clearError()
      selectedClient.value = null
      
      // Annuler la recherche précédente
      if (searchTimeout) {
        clearTimeout(searchTimeout)
      }
      
      const query = form.value.client.nomComplet
      
      // Minimum 2 caractères pour lancer la recherche
      if (!query || query.length < 2) {
        clientSuggestions.value = []
        showClientSuggestions.value = false
        return
      }
      
      // Debounce de 300ms
      searchTimeout = setTimeout(async () => {
        searchingClients.value = true
        try {
          const response = await api.get('/clients/search', {
            params: { q: query, limit: 8 }
          })
          clientSuggestions.value = response.data.data || []
          showClientSuggestions.value = clientSuggestions.value.length > 0
        } catch (error) {
          console.error('Erreur recherche clients:', error)
          clientSuggestions.value = []
        } finally {
          searchingClients.value = false
        }
      }, 300)
    }
    
    // Sélectionner un client existant
    const selectClient = (client) => {
      selectedClient.value = client
      
      // Remplir automatiquement les informations du client
      form.value.client.nomComplet = client.nomComplet
      form.value.client.telephone = client.telephone || ''
      form.value.client.email = client.email || ''
      form.value.client.matriculeFiscal = client.matriculeFiscal || ''
      form.value.client.codeLog = client.codeLog || ''
      
      if (client.adresse) {
        form.value.client.adresse.rue = client.adresse.rue || ''
        form.value.client.adresse.ville = client.adresse.ville || ''
        form.value.client.adresse.codePostal = client.adresse.codePostal || ''
        form.value.client.adresse.region = client.adresse.region || ''
      }
      
      // Fermer les suggestions
      clientSuggestions.value = []
      showClientSuggestions.value = false
      
      toast.success(`Client "${client.nomComplet}" sélectionné`)
    }
    
    // Cacher les suggestions avec un délai (pour permettre le clic)
    const hideClientSuggestionsDelayed = () => {
      setTimeout(() => {
        showClientSuggestions.value = false
      }, 200)
    }

    // Charger la liste des techniciens disponibles
    const loadTechniciens = async () => {
      try {
        const response = await api.get('/prospects/techniciens')
        techniciens.value = response.data.techniciens || []
        console.log('✅ Techniciens chargés:', techniciens.value.length)
      } catch (error) {
        console.error('❌ Erreur lors du chargement des techniciens:', error)
        toast.error('Erreur lors du chargement des techniciens')
      }
    }

    // Charger les techniciens disponibles selon la date et les heures
    const loadTechniciensDisponibles = async (dateSortie, heureDebut, heureFin) => {
      if (!dateSortie || !heureDebut || !heureFin) {
        await loadTechniciens()
        return
      }

      try {
        const response = await api.get('/prospects/techniciens/disponibles', {
          params: { 
            date: dateSortie,
            heureDebut: heureDebut,
            heureFin: heureFin
          }
        })
        techniciens.value = response.data.techniciensDisponibles || response.data.techniciens || []
        console.log('✅ Techniciens disponibles chargés:', techniciens.value.length, 'pour', dateSortie, heureDebut, '-', heureFin)
      } catch (error) {
        console.error('❌ Erreur lors du chargement des techniciens disponibles:', error)
        // Fallback: charger tous les techniciens
        await loadTechniciens()
      }
    }

    // Surveiller les changements de date de sortie pour recharger les techniciens disponibles
    watch(() => form.value.datesProposes, (newDates) => {
      // Trouver la première date de sortie renseignée avec heures
      const premiereDate = newDates.find(date => date.dateSortie && date.heureDebut && date.heureFin)
      if (premiereDate) {
        loadTechniciensDisponibles(premiereDate.dateSortie, premiereDate.heureDebut, premiereDate.heureFin)
      } else {
        loadTechniciens() // Charger tous les techniciens si aucune date complète
      }
    }, { deep: true })

    // Fonction pour calculer heureFin = heureDebut + 1h
    const calculateHeureFin = (heureDebut) => {
      if (!heureDebut) return '12:00'
      const [hours, minutes] = heureDebut.split(':').map(Number)
      const newHours = (hours + 1) % 24 // Ajouter 1 heure, gérer le passage à minuit
      return `${String(newHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    }

    // Surveiller les changements de heureDebut pour calculer automatiquement heureFin
    watch(() => form.value.datesProposes.map(d => d.heureDebut), (newHeures, oldHeures) => {
      newHeures.forEach((newHeure, index) => {
        // Vérifier si heureDebut a changé pour ce créneau
        if (newHeure && newHeure !== oldHeures?.[index]) {
          // Calculer automatiquement heureFin = heureDebut + 1h
          form.value.datesProposes[index].heureFin = calculateHeureFin(newHeure)
        }
      })
    }, { deep: true })

    // Charger les catégories depuis l'API
    const loadCategories = async () => {
      loadingCategories.value = true
      try {
        const response = await api.get('/categories', {
          params: { actif: true }
        })
        // Filtrer uniquement les catégories avec afficherDansProspects = true
        const allCategories = response.data.data || []
        categories.value = allCategories.filter(cat => cat.afficherDansProspects === true)
        console.log('✅ Catégories chargées pour Planning:', categories.value.length, '/', allCategories.length)
      } catch (error) {
        console.error('❌ Erreur chargement catégories:', error)
        toast.error('Erreur lors du chargement des catégories')
      } finally {
        loadingCategories.value = false
      }
    }

    // Charger les techniciens et catégories au montage du composant
    onMounted(() => {
      loadTechniciens()
      loadCategories()
    })

    return {
      form,
      submitting,
      errorMessage,
      techniciens,
      categories,
      loadingCategories,
      currentStep,
      totalSteps,
      steps,
      progressPercentage,
      minDate,
      validDatesCount,
      isStepValid,
      isFormValid,
      currentAppareil,
      canAddAppareil,
      handleAppareilTypeChange,
      addAppareil,
      removeAppareil,
      getAppareilIcon,
      nextStep,
      previousStep,
      addDate,
      removeDate,
      isTimePastForToday,
      getMinTimeForDate,
      submitForm,
      closeModal,
      clearError,
      userRole,
      // Recouvrement
      hasRecouvrementSelection,
      // Autocomplétion clients
      clientSuggestions,
      showClientSuggestions,
      searchingClients,
      selectedClient,
      onClientSearch,
      selectClient,
      hideClientSuggestionsDelayed
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 24px;
  width: 100%;
  max-width: 900px;
  max-height: 95vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  border-bottom: none;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  border-radius: 24px 24px 0 0;
  position: relative;
  overflow: hidden;
}

.header-content {
  flex: 1;
}

.header-content h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
}

.step-indicator {
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.9;
  font-weight: 500;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), transparent);
  pointer-events: none;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Barre de progression */
.progress-container {
  padding: 1rem 2rem;
  background: linear-gradient(135deg, 
    rgba(248, 250, 252, 0.95) 0%, 
    rgba(255, 255, 255, 0.9) 100%);
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: rgba(226, 232, 240, 0.3);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1rem;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  border-radius: 10px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: progressShine 2s ease-in-out infinite;
}

@keyframes progressShine {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.steps-indicators {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.step-dot {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  position: relative;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.step-dot.upcoming .step-number {
  background: rgba(226, 232, 240, 0.5);
  color: #64748b;
  border: 2px solid rgba(226, 232, 240, 0.8);
}

.step-dot.active .step-number {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  border: 2px solid transparent;
  box-shadow: 0 4px 15px rgba(0, 27, 183, 0.3);
  transform: scale(1.1);
  animation: pulse 2s ease-in-out infinite;
}

.step-dot.completed .step-number {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: 2px solid transparent;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

@keyframes pulse {
  0%, 100% { box-shadow: 0 4px 15px rgba(0, 27, 183, 0.3); }
  50% { box-shadow: 0 6px 25px rgba(0, 27, 183, 0.5); }
}

.step-label {
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  transition: all 0.3s ease;
}

.step-dot.upcoming .step-label {
  color: #64748b;
}

.step-dot.active .step-label {
  color: var(--primary-blue);
  transform: translateY(-2px);
}

.step-dot.completed .step-label {
  color: #10b981;
}

.modal-body {
  padding: 1.5rem 2rem;
  flex: 1;
  overflow-y: auto;
  position: relative;
  max-height: calc(95vh - 200px);
}

.form-section {
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8));
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-left: 4px solid var(--primary-blue);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.form-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.02), transparent);
  pointer-events: none;
}

.form-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 27, 183, 0.3);
}

.form-section h4 {
  margin: 0 0 1rem 0;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 1.1rem;
  font-weight: 600;
  position: relative;
}

.form-section h4::before {
  content: '';
  position: absolute;
  left: -1.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 20px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 2px;
}

.form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  flex: 1;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 12px;
  font-size: 0.9rem;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(5px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.form-control:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
  background: rgba(255, 255, 255, 0.95);
  transform: translateY(-1px);
}

.form-control:hover {
  border-color: rgba(0, 27, 183, 0.3);
  background: rgba(255, 255, 255, 0.9);
}

.dates-container {
  margin-bottom: 1rem;
}

.form-help {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1rem;
  font-style: italic;
}

.form-label-small {
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.date-row {
  display: flex;
  gap: 0.75rem;
  align-items: end;
  margin-bottom: 0.75rem;
  padding: 1rem;
  background: rgba(248, 250, 252, 0.5);
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.3s ease;
}

.date-row:hover {
  background: rgba(248, 250, 252, 0.8);
  border-color: rgba(0, 27, 183, 0.2);
}

.date-row-invalid {
  border-color: rgba(239, 68, 68, 0.3);
  background: rgba(254, 242, 242, 0.5);
}

.date-row-invalid:hover {
  border-color: rgba(239, 68, 68, 0.5);
  background: rgba(254, 242, 242, 0.8);
}

.form-control.is-invalid {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.form-control.is-invalid:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
}

.dates-summary {
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: rgba(16, 185, 129, 0.05);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 8px;
}

.text-success {
  color: #10b981;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Error Banner */
.error-banner {
  margin: 0 1.5rem 1rem 1.5rem;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.05));
  border: 1px solid rgba(239, 68, 68, 0.3);
  animation: slideDown 0.3s ease;
}

.error-content {
  display: flex;
  align-items: center;
  padding: 1rem;
  gap: 0.75rem;
  color: #dc2626;
  font-weight: 500;
}

.error-content i.fa-exclamation-triangle {
  color: #ef4444;
  font-size: 1.1rem;
}

.error-content span {
  flex: 1;
}

.error-close {
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.error-close:hover {
  background: rgba(239, 68, 68, 0.1);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.btn-remove {
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-remove:hover {
  background: #c82333;
}

/* Styles des étapes */
.step-content {
  opacity: 0;
  transform: translateX(30px);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  pointer-events: none;
}

.step-content.step-active {
  opacity: 1;
  transform: translateX(0);
  position: relative;
  pointer-events: all;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, 
    rgba(0, 27, 183, 0.05) 0%, 
    rgba(0, 70, 255, 0.02) 100%);
  border-radius: 16px;
  border: 1px solid rgba(0, 27, 183, 0.1);
}

.step-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  box-shadow: 0 4px 15px rgba(0, 27, 183, 0.3);
}

.step-title h4 {
  margin: 0 0 0.25rem 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--primary-blue);
}

.step-description {
  margin: 0;
  font-size: 0.9rem;
  color: #64748b;
  font-style: italic;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  border-top: 1px solid rgba(226, 232, 240, 0.5);
  background: linear-gradient(135deg, 
    rgba(248, 250, 252, 0.95) 0%, 
    rgba(255, 255, 255, 0.9) 100%);
  backdrop-filter: blur(15px);
  border-radius: 0 0 24px 24px;
}

.footer-left, .footer-right {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.85rem;
}

.btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn:hover::before {
  left: 100%;
}

.btn-primary {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  box-shadow: 0 4px 15px rgba(0, 27, 183, 0.3);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 8px rgba(0, 27, 183, 0.2);
}

.btn-outline {
  background: transparent;
  color: var(--primary-blue);
  border: 2px solid var(--primary-blue);
  box-shadow: 0 2px 8px rgba(0, 27, 183, 0.1);
}

.btn-outline:hover:not(:disabled) {
  background: var(--primary-blue);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 27, 183, 0.3);
}

.btn-secondary {
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white !important;
  border: 2px solid white !important;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
}

.btn-secondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(220, 53, 69, 0.4);
  color: white !important;
  border: 2px solid white !important;
}

.btn-secondary:focus {
  color: white !important;
  border: 2px solid white !important;
  outline: none;
}

.btn-secondary:active {
  color: white !important;
  border: 2px solid white !important;
}

.btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.btn-success:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
}

.btn-success:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .modal-content {
    max-width: 90vw;
    margin: 1rem;
  }
  
  .steps-indicators {
    gap: 0.5rem;
  }
  
  .step-label {
    font-size: 0.7rem;
  }
}

@media (max-width: 768px) {
  .modal-content {
    margin: 0.25rem;
    max-height: 98vh;
    border-radius: 16px;
  }
  
  .modal-header {
    padding: 1rem;
    border-radius: 16px 16px 0 0;
  }
  
  .header-content h3 {
    font-size: 1.1rem;
  }
  
  .progress-container {
    padding: 0.75rem 1rem;
  }
  
  .step-number {
    width: 32px;
    height: 32px;
    font-size: 0.8rem;
  }
  
  .step-label {
    font-size: 0.65rem;
  }
  
  .modal-body {
    padding: 1.5rem;
    min-height: 300px;
  }
  
  .step-header {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
    padding: 1rem;
  }
  
  .step-icon {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
  
  .form-row {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .date-row {
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
  }
  
  .modal-footer {
    padding: 1.5rem;
    flex-direction: column;
    gap: 1rem;
  }
  
  .footer-left, .footer-right {
    width: 100%;
    justify-content: center;
  }
  
  .btn {
    padding: 0.875rem 1.5rem;
    font-size: 0.9rem;
    min-width: 120px;
  }
}

@media (max-width: 480px) {
  .modal-content {
    margin: 0.25rem;
    max-height: 98vh;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .header-content h3 {
    font-size: 1.1rem;
  }
  
  .step-indicator {
    font-size: 0.8rem;
  }
  
  .progress-container {
    padding: 0.75rem;
  }
  
  .steps-indicators {
    flex-wrap: wrap;
    gap: 0.25rem;
  }
  
  .step-dot {
    flex: none;
    min-width: 60px;
  }
  
  .step-number {
    width: 28px;
    height: 28px;
    font-size: 0.7rem;
  }
  
  .step-label {
    font-size: 0.6rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
  
  .step-header {
    padding: 0.75rem;
  }
  
  .step-title h4 {
    font-size: 1rem;
  }
  
  .step-description {
    font-size: 0.8rem;
  }
  
  .form-section {
    padding: 1rem;
    margin-bottom: 1rem;
  }
  
  .btn {
    padding: 0.75rem 1.25rem;
    font-size: 0.85rem;
    min-width: 100px;
  }
  
  /* Intervention & Priority Selectors Responsive */
  .intervention-selector-wrapper,
  .priority-selector-wrapper {
    min-height: 48px;
  }
  
  .intervention-selector-icon,
  .priority-selector-icon {
    width: 44px;
    height: 44px;
    font-size: 0.9rem;
  }
  
  .intervention-selector-wrapper select,
  .priority-selector-wrapper select {
    font-size: 0.8rem;
    padding: 0.6rem 0.8rem !important;
  }
  
  .intervention-selector-arrow,
  .priority-selector-arrow {
    width: 36px;
    height: 36px;
    font-size: 0.8rem;
  }
  
  .intervention-selection-indicator,
  .priority-selection-indicator {
    width: 20px;
    height: 20px;
    font-size: 0.6rem;
    top: -6px;
    right: -6px;
  }
  
  .intervention-selection-badge,
  .priority-selection-badge {
    width: 18px;
    height: 18px;
    font-size: 0.6rem;
    top: -4px;
    right: 6px;
  }
}

/* Intervention Selector Ultra-Professional Design */
.intervention-selector-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95));
  backdrop-filter: blur(15px);
  border: 2px solid rgba(0, 27, 183, 0.12);
  border-radius: 16px;
  padding: 0;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 8px 32px rgba(0, 27, 183, 0.06);
  overflow: hidden;
  min-height: 52px;
}

.intervention-selector-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.02), rgba(0, 70, 255, 0.01));
  z-index: 0;
}

.intervention-selector-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 27, 183, 0.12);
  border-color: rgba(0, 27, 183, 0.2);
}

.intervention-selector-wrapper:focus-within {
  transform: translateY(-3px);
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.08), 0 15px 45px rgba(0, 27, 183, 0.15);
}

.intervention-selector-wrapper:focus-within .intervention-selector-arrow i {
  transform: rotate(180deg);
}

.intervention-selector-wrapper.error {
  border-color: #dc3545;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.intervention-selector-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border-radius: 12px;
  margin: 2px;
  font-size: 1rem;
  position: relative;
  z-index: 2;
  box-shadow: 0 4px 16px rgba(0, 27, 183, 0.25);
  transition: all 0.3s ease;
}

.intervention-selector-wrapper.has-selection .intervention-selector-icon {
  background: linear-gradient(135deg, #10b981, #059669);
  animation: pulse 2s infinite;
}

.intervention-selector-wrapper select {
  flex: 1;
  background: transparent !important;
  border: none !important;
  padding: 0.75rem 1rem !important;
  font-size: 0.85rem;
  color: var(--primary-blue);
  font-weight: 600;
  position: relative;
  z-index: 1;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.intervention-selector-wrapper select:focus {
  outline: none;
  box-shadow: none !important;
  transform: none !important;
  border: none !important;
}

.intervention-selector-wrapper.has-selection select {
  color: var(--secondary-blue);
  font-weight: 700;
}

.intervention-selector-wrapper select option {
  background: white;
  color: #333;
  padding: 0.75rem;
  font-size: 0.85rem;
  border-radius: 8px;
  margin: 2px 0;
}

.intervention-selector-wrapper select option:first-child {
  color: #6c757d;
  font-style: italic;
}

.intervention-selector-wrapper select option:not(:first-child) {
  font-weight: 600;
}

.intervention-selector-wrapper select option:hover {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05), rgba(0, 70, 255, 0.02));
}

.intervention-selector-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: var(--primary-blue);
  font-size: 0.9rem;
  position: relative;
  z-index: 2;
  margin-right: 6px;
  transition: all 0.3s ease;
}

.intervention-selector-arrow i {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.intervention-selector-wrapper.has-selection .intervention-selector-arrow {
  color: var(--secondary-blue);
}

.intervention-selection-indicator {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.7rem;
  z-index: 3;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  animation: pulse 2s infinite;
}

.intervention-selection-badge {
  position: absolute;
  top: -6px;
  right: 8px;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  z-index: 4;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
  animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

/* Priority Selector Ultra-Professional Design */
.priority-selector-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95));
  backdrop-filter: blur(15px);
  border: 2px solid rgba(255, 152, 0, 0.12);
  border-radius: 16px;
  padding: 0;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 8px 32px rgba(255, 152, 0, 0.06);
  overflow: hidden;
  min-height: 52px;
}

.priority-selector-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.02), rgba(255, 193, 7, 0.01));
  z-index: 0;
}

.priority-selector-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(255, 152, 0, 0.12);
  border-color: rgba(255, 152, 0, 0.2);
}

.priority-selector-wrapper:focus-within {
  transform: translateY(-3px);
  border-color: #ff9800;
  box-shadow: 0 0 0 4px rgba(255, 152, 0, 0.08), 0 15px 45px rgba(255, 152, 0, 0.15);
}

.priority-selector-wrapper:focus-within .priority-selector-arrow i {
  transform: rotate(180deg);
}

.priority-selector-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #ff9800, #f57c00);
  color: white;
  border-radius: 12px;
  margin: 2px;
  font-size: 1rem;
  position: relative;
  z-index: 2;
  box-shadow: 0 4px 16px rgba(255, 152, 0, 0.25);
  transition: all 0.3s ease;
}

.priority-selector-wrapper.has-selection .priority-selector-icon {
  background: linear-gradient(135deg, #10b981, #059669);
  animation: pulse 2s infinite;
}

.priority-selector-wrapper select {
  flex: 1;
  background: transparent !important;
  border: none !important;
  padding: 0.75rem 1rem !important;
  font-size: 0.85rem;
  color: #ff9800;
  font-weight: 600;
  position: relative;
  z-index: 1;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.priority-selector-wrapper select:focus {
  outline: none;
  box-shadow: none !important;
  transform: none !important;
  border: none !important;
}

.priority-selector-wrapper.has-selection select {
  color: #f57c00;
  font-weight: 700;
}

.priority-selector-wrapper select option {
  background: white;
  color: #333;
  padding: 0.75rem;
  font-size: 0.85rem;
  border-radius: 8px;
  margin: 2px 0;
  font-weight: 600;
}

.priority-selector-wrapper select option:hover {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.05), rgba(255, 193, 7, 0.02));
}

.priority-selector-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: #ff9800;
  font-size: 0.9rem;
  position: relative;
  z-index: 2;
  margin-right: 6px;
  transition: all 0.3s ease;
}

.priority-selector-arrow i {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.priority-selector-wrapper.has-selection .priority-selector-arrow {
  color: #f57c00;
}

.priority-selection-indicator {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.7rem;
  z-index: 3;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  animation: pulse 2s infinite;
}

.priority-selection-badge {
  position: absolute;
  top: -6px;
  right: 8px;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  z-index: 4;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
  animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

@keyframes bounceIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Appareil Selector Ultra-Professional Design */
.appareil-selector-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 16px;
  padding: 0.75rem 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(0, 27, 183, 0.08);
  min-height: 52px;
}

.appareil-selector-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.02), rgba(0, 70, 255, 0.01));
  border-radius: 16px;
  z-index: 0;
}

.appareil-selector-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 27, 183, 0.12);
  border-color: rgba(0, 27, 183, 0.2);
}

.appareil-selector-wrapper:focus-within {
  transform: translateY(-3px);
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.08), 0 15px 45px rgba(0, 27, 183, 0.15);
}

.appareil-selector-wrapper:focus-within .appareil-selector-arrow i {
  transform: rotate(180deg);
}

.appareil-selector-wrapper.error {
  border-color: #dc3545;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.appareil-selector-icon {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: linear-gradient(135deg, #6b7280, #4b5563);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  color: white;
  font-size: 0.8rem;
  z-index: 1;
  transition: all 0.3s ease;
}

.appareil-selector-wrapper.has-selection .appareil-selector-icon {
  background: linear-gradient(135deg, #10b981, #059669);
  animation: pulse 2s infinite;
}

.appareil-selector-wrapper select {
  flex: 1;
  background: transparent !important;
  border: none !important;
  outline: none !important;
  font-size: 0.95rem;
  font-weight: 500;
  color: #374151;
  padding: 0 !important;
  margin: 0 !important;
  z-index: 1;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.appareil-selector-wrapper select:focus {
  outline: none;
  box-shadow: none !important;
  transform: none !important;
  border: none !important;
}

.appareil-selector-wrapper.has-selection select {
  color: var(--secondary-blue);
  font-weight: 700;
}

.appareil-selector-wrapper select option {
  background: white;
  color: #333;
  padding: 0.75rem;
  font-weight: 500;
  border-radius: 8px;
  margin: 2px 0;
}

.appareil-selector-wrapper select option:first-child {
  color: #6c757d;
  font-style: italic;
}

.appareil-selector-wrapper select option:not(:first-child) {
  font-weight: 600;
}

.appareil-selector-wrapper select option:hover {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05), rgba(0, 70, 255, 0.02));
}

.appareil-selector-arrow {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 0.8rem;
  z-index: 1;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.appareil-selector-wrapper.has-selection .appareil-selector-arrow {
  color: var(--secondary-blue);
}

.appareil-selection-indicator {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.7rem;
  z-index: 2;
  animation: bounceIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.appareil-selection-badge {
  position: absolute;
  top: -6px;
  right: 15px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
  z-index: 3;
  animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
}

/* Hover effect with shine animation */
.intervention-selector-wrapper:hover::after,
.priority-selector-wrapper:hover::after,
.appareil-selector-wrapper:hover::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: shine 0.8s ease-out;
  z-index: 1;
}

@keyframes shine {
  0% { left: -100%; }
  100% { left: 100%; }
}

/* Custom Appareil Input */
.custom-appareil-row {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.custom-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 0 16px;
  transition: all 0.3s ease;
}

.custom-input-wrapper:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
}

.custom-input-wrapper:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.custom-input-wrapper i {
  color: #3b82f6;
  font-size: 18px;
}

.custom-input-wrapper input {
  flex: 1;
  border: none !important;
  background: transparent !important;
  padding: 14px 0 !important;
  font-size: 15px;
  font-weight: 500;
  color: #1e293b;
  box-shadow: none !important;
}

.custom-input-wrapper input:focus {
  outline: none;
  box-shadow: none !important;
}

.custom-input-wrapper input::placeholder {
  color: #94a3b8;
  font-weight: 400;
}

@media (max-width: 480px) {
  .intervention-selector-wrapper,
  .priority-selector-wrapper,
  .appareil-selector-wrapper {
    min-height: 44px;
    border-radius: 12px;
  }
  
  .intervention-selector-icon,
  .priority-selector-icon,
  .appareil-selector-icon {
    width: 40px;
    height: 40px;
    font-size: 0.85rem;
    border-radius: 10px;
  }
  
  .intervention-selector-wrapper select,
  .priority-selector-wrapper select,
  .appareil-selector-wrapper select {
    font-size: 0.75rem;
    padding: 0.5rem 0.75rem !important;
  }
  
  .intervention-selector-arrow,
  .priority-selector-arrow,
  .appareil-selector-arrow,
  .technician-selector-arrow {
    width: 32px;
    height: 32px;
    font-size: 0.75rem;
    margin-right: 4px;
  }
  
  .technician-selector-wrapper {
    min-height: 44px;
    border-radius: 12px;
  }
  
  .technician-selector-icon {
    width: 40px;
    height: 40px;
    font-size: 0.85rem;
    border-radius: 10px;
  }
  
  .technician-selector-wrapper select {
    font-size: 0.75rem;
    padding: 0.5rem 0.75rem !important;
  }
}

/* Technician Selector Ultra-Professional Design */
.technician-selector-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95));
  backdrop-filter: blur(15px);
  border: 2px solid rgba(16, 185, 129, 0.12);
  border-radius: 16px;
  padding: 0;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 8px 32px rgba(16, 185, 129, 0.06);
  overflow: hidden;
  min-height: 52px;
}

.technician-selector-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.02), rgba(5, 150, 105, 0.01));
  z-index: 0;
}

.technician-selector-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(16, 185, 129, 0.12);
  border-color: rgba(16, 185, 129, 0.2);
}

.technician-selector-wrapper:focus-within {
  transform: translateY(-3px);
  border-color: #10b981;
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.08), 0 15px 45px rgba(16, 185, 129, 0.15);
}

.technician-selector-wrapper:focus-within .technician-selector-arrow i {
  transform: rotate(180deg);
}

.technician-selector-wrapper.error {
  border-color: #dc3545;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.technician-selector-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border-radius: 12px;
  margin: 2px;
  font-size: 1rem;
  position: relative;
  z-index: 2;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.25);
  transition: all 0.3s ease;
}

.technician-selector-wrapper.has-selection .technician-selector-icon {
  background: linear-gradient(135deg, #059669, #047857);
  animation: pulse 2s infinite;
}

.technician-selector-wrapper select {
  flex: 1;
  background: transparent !important;
  border: none !important;
  padding: 0.75rem 1rem !important;
  font-size: 0.85rem;
  color: #10b981;
  font-weight: 600;
  position: relative;
  z-index: 1;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.technician-selector-wrapper select:focus {
  outline: none;
  box-shadow: none !important;
  transform: none !important;
  border: none !important;
}

.technician-selector-wrapper.has-selection select {
  color: #059669;
  font-weight: 700;
}

.technician-selector-wrapper select option {
  background: white;
  color: #333;
  padding: 0.75rem;
  font-size: 0.85rem;
  border-radius: 8px;
  margin: 2px 0;
}

.technician-selector-wrapper select option:first-child {
  color: #6c757d;
  font-style: italic;
}

.technician-selector-wrapper select option:not(:first-child) {
  font-weight: 600;
}

.technician-selector-wrapper select option:hover {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(5, 150, 105, 0.02));
}

.technician-selector-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: #10b981;
  font-size: 0.9rem;
  position: relative;
  z-index: 2;
  margin-right: 6px;
  transition: all 0.3s ease;
}

.technician-selector-arrow i {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.technician-selector-wrapper.has-selection .technician-selector-arrow {
  color: #059669;
}

.technician-selection-indicator {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.7rem;
  z-index: 3;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  animation: pulse 2s infinite;
}

.technician-selection-badge {
  position: absolute;
  top: -6px;
  right: 8px;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  z-index: 4;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
  animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

/* Hover effect with shine animation for technician selector */
.technician-selector-wrapper:hover::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: shine 0.8s ease-out;
  z-index: 1;
}

/* Update responsive styles to include technician selector */
@media (max-width: 768px) {
  .intervention-selector-wrapper,
  .priority-selector-wrapper,
  .appareil-selector-wrapper,
  .technician-selector-wrapper {
    min-height: 48px;
  }
  
  .intervention-selector-icon,
  .priority-selector-icon,
  .appareil-selector-icon,
  .technician-selector-icon {
    width: 44px;
    height: 44px;
    font-size: 0.9rem;
  }
  
  .intervention-selector-wrapper select,
  .priority-selector-wrapper select,
  .appareil-selector-wrapper select,
  .technician-selector-wrapper select {
    font-size: 0.8rem;
    padding: 0.6rem 0.8rem !important;
  }
  
  .intervention-selector-arrow,
  .priority-selector-arrow,
  .appareil-selector-arrow,
  .technician-selector-arrow {
    width: 36px;
    height: 36px;
    font-size: 0.8rem;
  }
  
  .intervention-selection-indicator,
  .priority-selection-indicator,
  .technician-selection-indicator {
    width: 20px;
    height: 20px;
    font-size: 0.6rem;
    top: -6px;
    right: -6px;
  }
  
  .intervention-selection-badge,
  .priority-selection-badge,
  .technician-selection-badge {
    width: 18px;
    height: 18px;
    font-size: 0.6rem;
    top: -4px;
    right: 6px;
  }
}

/* Styles pour la liste d'appareils */
.appareils-list {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.03), rgba(5, 150, 105, 0.02));
  border-radius: 16px;
  border: 2px solid rgba(16, 185, 129, 0.1);
}

.list-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  color: #059669;
  font-size: 0.95rem;
  margin-bottom: 0.75rem;
}

.list-label i {
  font-size: 1rem;
}

.appareil-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: white;
  border-radius: 12px;
  margin-bottom: 0.5rem;
  border: 2px solid rgba(226, 232, 240, 0.5);
  transition: all 0.3s ease;
}

.appareil-item:last-child {
  margin-bottom: 0;
}

.appareil-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: rgba(16, 185, 129, 0.3);
}

.appareil-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.appareil-icon {
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.05));
  border-radius: 10px;
}

.appareil-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.95rem;
}

.appareil-quantity {
  font-weight: 700;
  color: #059669;
  font-size: 1rem;
  padding: 0.25rem 0.75rem;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.05));
  border-radius: 8px;
}

.btn-remove-appareil {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-remove-appareil:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  background: linear-gradient(135deg, #dc2626, #b91c1c);
}

/* Formulaire d'ajout d'appareil */
.add-appareil-form {
  padding: 1.25rem;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95));
  border-radius: 16px;
  border: 2px dashed rgba(100, 116, 139, 0.2);
  margin-bottom: 1.5rem;
}

.form-label-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  color: var(--primary-blue);
  font-size: 1rem;
  margin-bottom: 1rem;
}

.form-label-section i {
  font-size: 1.1rem;
  color: var(--secondary-blue);
}

.btn-add-appareil {
  width: 100%;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.2);
}

.btn-add-appareil:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 27, 183, 0.3);
}

.btn-add-appareil:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-add-appareil i {
  font-size: 1rem;
}

@media (max-width: 768px) {
  .appareils-list {
    padding: 0.75rem;
  }
  
  .appareil-item {
    padding: 0.6rem 0.75rem;
  }
  
  .appareil-icon {
    width: 36px;
    height: 36px;
    font-size: 1.25rem;
  }
  
  .appareil-name {
    font-size: 0.85rem;
  }
  
  .appareil-quantity {
    font-size: 0.9rem;
    padding: 0.2rem 0.6rem;
  }
  
  .btn-remove-appareil {
    padding: 0.4rem 0.6rem;
    font-size: 0.8rem;
  }
  
  .add-appareil-form {
    padding: 1rem;
  }
}

/* Styles pour les champs invalides et messages d'erreur */
.form-control.is-invalid {
  border-color: #ef4444;
  background-color: rgba(239, 68, 68, 0.05);
}

.form-control.is-invalid:focus {
  border-color: #dc2626;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.text-danger {
  color: #ef4444;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
}

.text-info {
  color: #3b82f6;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
}

.text-success {
  color: #10b981;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
}

/* ========================================
   Gouvernorat Selector - Professional Design
   ======================================== */
.gouvernorat-select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.03), rgba(37, 99, 235, 0.02));
  border: 2px solid rgba(59, 130, 246, 0.15);
  border-radius: 16px;
  padding: 0.25rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.08);
  overflow: hidden;
}

.gouvernorat-select-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 0;
}

.gouvernorat-select-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.3);
}

.gouvernorat-select-wrapper:hover::before {
  opacity: 1;
}

.gouvernorat-select-wrapper:focus-within {
  transform: translateY(-3px);
  border-color: #3b82f6;
  box-shadow: 0 16px 50px rgba(59, 130, 246, 0.2), 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.gouvernorat-select-wrapper:focus-within .gouvernorat-select-arrow i {
  transform: rotate(180deg);
}

.gouvernorat-select-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-radius: 12px;
  color: white;
  font-size: 1.1rem;
  flex-shrink: 0;
  z-index: 2;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  transition: all 0.3s ease;
}

.gouvernorat-select-wrapper.has-selection .gouvernorat-select-icon {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  animation: pulse 2s infinite;
}

.gouvernorat-select-wrapper select {
  flex: 1;
  background: transparent !important;
  border: none !important;
  padding: 0.75rem 1rem !important;
  font-size: 0.95rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  z-index: 2;
  min-height: 48px;
}

.gouvernorat-select-wrapper select:focus {
  outline: none;
  box-shadow: none !important;
  border: none !important;
}

.gouvernorat-select-wrapper.has-selection select {
  color: #2563eb;
  font-weight: 700;
}

.gouvernorat-select-wrapper select option {
  background: white;
  color: #333;
  padding: 12px 16px;
  font-weight: 500;
}

.gouvernorat-select-wrapper select optgroup {
  font-weight: 700;
  color: #1e40af;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05));
  padding: 8px 0;
}

.gouvernorat-select-wrapper select option:first-child {
  color: #6c757d;
  font-style: italic;
}

.gouvernorat-select-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05));
  border-radius: 10px;
  color: #3b82f6;
  font-size: 0.9rem;
  flex-shrink: 0;
  z-index: 2;
  margin-right: 0.25rem;
  transition: all 0.3s ease;
}

.gouvernorat-select-arrow i {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.gouvernorat-select-wrapper.has-selection .gouvernorat-select-arrow {
  color: #2563eb;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.15), rgba(29, 78, 216, 0.1));
}

.gouvernorat-selection-indicator {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.7rem;
  z-index: 5;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
  animation: bounceIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

/* Hover effect with shine animation */
.gouvernorat-select-wrapper:hover::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: shine 0.8s ease-out;
  z-index: 1;
}

/* Responsive styles for gouvernorat selector */
@media (max-width: 768px) {
  .gouvernorat-select-wrapper {
    min-height: 48px;
  }
  
  .gouvernorat-select-icon {
    width: 42px;
    height: 42px;
    font-size: 0.95rem;
  }
  
  .gouvernorat-select-wrapper select {
    font-size: 0.85rem;
    padding: 0.6rem 0.8rem !important;
  }
  
  .gouvernorat-select-arrow {
    width: 36px;
    height: 36px;
    font-size: 0.8rem;
  }
  
  .gouvernorat-selection-indicator {
    width: 20px;
    height: 20px;
    font-size: 0.6rem;
    top: -6px;
    right: -6px;
  }
}

/* ===== AUTOCOMPLÉTION CLIENTS ===== */
.client-autocomplete-group {
  position: relative;
}

.autocomplete-wrapper {
  position: relative;
}

.autocomplete-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.autocomplete-icon .text-success {
  color: #10b981;
}

.autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 320px;
  overflow-y: auto;
  margin-top: 4px;
  animation: dropdownSlide 0.2s ease-out;
}

@keyframes dropdownSlide {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
}

.dropdown-header i {
  color: #001BB7;
}

.autocomplete-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f1f5f9;
}

.autocomplete-item:last-child {
  border-bottom: none;
}

.autocomplete-item:hover {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05) 0%, rgba(0, 70, 255, 0.05) 100%);
}

.client-avatar {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #001BB7 0%, #0046FF 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.client-info {
  flex: 1;
  min-width: 0;
}

.client-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.client-details {
  display: flex;
  gap: 1rem;
  font-size: 0.75rem;
  color: #64748b;
}

.client-details span {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.client-details i {
  font-size: 0.65rem;
  color: #94a3b8;
}

.client-select-icon {
  color: #cbd5e1;
  transition: all 0.2s ease;
}

.autocomplete-item:hover .client-select-icon {
  color: #001BB7;
  transform: translateX(3px);
}

.client-selected-hint {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #10b981;
  font-weight: 500;
}

.client-selected-hint i {
  font-size: 0.8rem;
}

/* Responsive */
@media (max-width: 768px) {
  .autocomplete-dropdown {
    max-height: 250px;
  }
  
  .autocomplete-item {
    padding: 0.75rem;
  }
  
  .client-avatar {
    width: 36px;
    height: 36px;
    font-size: 0.8rem;
  }
  
  .client-details {
    flex-direction: column;
    gap: 0.25rem;
  }
}

/* ===== SECTION RECOUVREMENT ===== */
.recouvrement-section {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 2px solid #f59e0b;
}

.recouvrement-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.recouvrement-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.recouvrement-title h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #92400e;
}

.recouvrement-title p {
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: #b45309;
}

.recouvrement-checklist {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.recouvrement-item {
  cursor: pointer;
  display: block;
}

.recouvrement-checkbox {
  display: none;
}

.recouvrement-item-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: white;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  border: 2px solid #e5e7eb;
  transition: all 0.3s ease;
}

.recouvrement-item:hover .recouvrement-item-content {
  border-color: #f59e0b;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.15);
}

.recouvrement-item.checked .recouvrement-item-content {
  border-color: #10b981;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}

.recouvrement-item-icon {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 1.2rem;
  transition: all 0.3s ease;
}

.recouvrement-item.checked .recouvrement-item-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.recouvrement-item-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.recouvrement-item-title {
  font-weight: 600;
  font-size: 1rem;
  color: #1e293b;
}

.recouvrement-item-desc {
  font-size: 0.8rem;
  color: #64748b;
}

.recouvrement-item-check {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: transparent;
  transition: all 0.3s ease;
}

.recouvrement-item.checked .recouvrement-item-check {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-color: #10b981;
  color: white;
}

.recouvrement-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: #fef2f2;
  border-radius: 8px;
  color: #dc2626;
  font-size: 0.85rem;
  font-weight: 500;
}

.recouvrement-warning i {
  font-size: 1rem;
}

@media (max-width: 768px) {
  .recouvrement-section {
    padding: 1rem;
  }
  
  .recouvrement-item-content {
    padding: 0.875rem 1rem;
  }
  
  .recouvrement-item-icon {
    width: 38px;
    height: 38px;
    font-size: 1rem;
  }
}
</style>
